import { useCallback, useState } from "react";

const STORAGE_PREFIX = "fraction:flag:";

/**
 * A boolean that survives reloads, keyed by name in localStorage. Built for
 * disclosure state (which sidebar sections a user keeps open) — cheap enough to
 * call once per section, and it degrades to plain `useState` when storage is
 * unavailable or corrupted.
 */
export function usePersistedFlag(key: string, defaultValue = false) {
  const [value, setValue] = useState(() => {
    if (typeof localStorage === "undefined") return defaultValue;
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + key);
      return raw === null ? defaultValue : raw === "true";
    } catch {
      return defaultValue;
    }
  });

  const set = useCallback(
    (next: boolean) => {
      setValue(next);
      try {
        localStorage.setItem(STORAGE_PREFIX + key, String(next));
      } catch {
        /* full or disabled storage — the toggle still applied this session */
      }
    },
    [key]
  );

  const toggle = useCallback(() => set(!value), [set, value]);

  return [value, set, toggle] as const;
}
